import { useCallback, useEffect, useRef, useState } from "react";
import { listStaffNotes } from "./staffNotesService";
export default function useMyStaffNotes(childId, enabled, userId, loadNotes = listStaffNotes) {
  const [state, setState] = useState({
    loading: true,
    notes: [],
    context: null,
    error: "",
  });
  const generation = useRef(0);
  const refresh = useCallback(async () => {
    const current = ++generation.current;
    if (!childId || !enabled) {
      setState({
        loading: false,
        notes: [],
        context: null,
        error: "Staff notes are unavailable for this profile.",
      });
      return;
    }
    setState((previous) => ({ ...previous, loading: true, error: "" }));
    try {
      const result = await loadNotes(childId);
      if (current === generation.current)
        setState({ ...result, loading: false, error: "" });
    } catch (error) {
      if (current === generation.current)
        setState((previous) =>
          previous.context?.childId === childId &&
          previous.context?.uid === userId
            ? {
                ...previous,
                loading: false,
                error:
                  error.message ||
                  "Notes could not be refreshed. Showing the last loaded originals.",
              }
            : {
                loading: false,
                notes: [],
                context: null,
                error: error.message || "Notes could not be loaded.",
              },
        );
    }
  }, [childId, enabled, userId, loadNotes]);
  useEffect(() => {
    refresh();
    return () => {
      generation.current += 1;
    };
  }, [refresh]);
  useEffect(() => {
    const changed = (event) => {
      if (!event.detail?.childId || event.detail.childId === childId) refresh();
    };
    window.addEventListener("captureez:staff-notes-refresh", changed);
    return () => window.removeEventListener("captureez:staff-notes-refresh", changed);
  }, [childId, refresh]);
  useEffect(() => {
    if (!enabled) return undefined;
    const pending = state.notes.some((note) =>
      ["pending", "working"].includes(note.processing?.status),
    );
    const timer = setTimeout(
      () => {
        if (document.visibilityState !== "hidden") refresh();
      },
      pending ? 5000 : 30000,
    );
    const resume = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", resume);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [enabled, state.notes, refresh]);
  if (!enabled)
    return { loading: false, notes: [], context: null, error: "", refresh };
  if (
    state.context &&
    (state.context.childId !== childId || state.context.uid !== userId)
  )
    return { loading: true, notes: [], context: null, error: "", refresh };
  return { ...state, refresh };
}
